/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx, css } from '@emotion/react'
import { useState } from 'react'
import moment from 'moment'

import {
  EuiButtonEmpty,
  EuiFlexGrid,
  EuiFlexItem,
  EuiFormFieldset,
  EuiIcon,
  EuiLink,
  EuiPopover,
  EuiSpacer,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import type { Moment } from 'moment'

const ranges = [
  {
    label: (
      <FormattedMessage id='commonly-used-ranges.current-week' defaultMessage='Current week' />
    ),
    key: 'current-week',
    start: moment.utc().startOf('week'),
    end: moment.utc().endOf('day'),
  },
  {
    label: <FormattedMessage id='commonly-used-ranges.last-7days' defaultMessage='Last 7 days' />,
    key: 'last-7-days',
    start: moment.utc().subtract(7, 'd').startOf('day'),
    end: moment.utc().endOf('day'),
  },
  {
    label: (
      <FormattedMessage id='commonly-used-ranges.current-month' defaultMessage='Current month' />
    ),
    key: 'current-month',
    start: moment.utc().startOf('month'),
    end: moment.utc().endOf('day'),
  },
  {
    label: <FormattedMessage id='commonly-used-ranges.last-30days' defaultMessage='Last 30 days' />,
    key: 'last-30-days',
    start: moment.utc().subtract(30, 'd').startOf('day'),
    end: moment.utc().endOf('day'),
  },
  {
    label: (
      <FormattedMessage id='commonly-used-ranges.previous-month' defaultMessage='Previous month' />
    ),
    key: 'previous-month',
    start: moment.utc().subtract(1, 'months').startOf('month'),
    end: moment.utc().subtract(1, 'months').endOf('month'),
  },
  {
    label: <FormattedMessage id='commonly-used-ranges.last-90days' defaultMessage='Last 90 days' />,
    key: 'last-90-days',
    start: moment.utc().subtract(90, 'd').startOf('day'),
    end: moment.utc().endOf('day'),
  },
]

interface Props {
  onChange: ({ startDate, endDate }: { startDate: Moment; endDate: Moment }) => void
}

const CommonlyUsedRanges = ({ onChange }: Props) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  const togglePopover = () => {
    setIsPopoverOpen(!isPopoverOpen)
  }

  const closePopover = () => {
    setIsPopoverOpen(false)
  }

  return (
    <EuiPopover
      button={
        <EuiButtonEmpty iconType='calendar' iconSide='left' onClick={togglePopover}>
          <EuiIcon type='arrowDown' size='s' />
        </EuiButtonEmpty>
      }
      isOpen={isPopoverOpen}
      closePopover={closePopover}
      anchorPosition='downLeft'
      data-test-id='commonly-used-selector'
    >
      <EuiFormFieldset
        legend={{
          children: (
            <FormattedMessage id='commonly-used-ranges.title' defaultMessage='Commonly used' />
          ),
        }}
      >
        <EuiSpacer size='s' />
        <EuiFlexGrid columns={2} gutterSize='s' css={css({ width: 285 })}>
          {ranges.map(({ label, key, start, end }) => (
            <EuiFlexItem key={key}>
              <EuiLink
                data-test-id={`commonly-used_${key}`}
                onClick={() => {
                  onChange({ startDate: start, endDate: end })
                  closePopover()
                }}
              >
                {label}
              </EuiLink>
            </EuiFlexItem>
          ))}
        </EuiFlexGrid>
      </EuiFormFieldset>
    </EuiPopover>
  )
}

export default CommonlyUsedRanges
